import { fieldRefEnd, truth } from './fr3Expr';
import { isLetter, isLetterOrDigit, isWhitespace, javaTrim } from './javaCompat';

/**
 * Interpreter for the PascalScript subset the templates use (port of
 * Fr3Script): variable declarations, assignment, if/else, begin/end, property
 * writes and calls. Every procedure in the corpus is an OnBeforePrint handler.
 *
 * objects (ObjectAccess): { getProperty(obj, prop), setProperty(obj, prop, value),
 * invoke(obj, method, args), isObject(name) }.
 */
export class Fr3Script {
    constructor(scriptText) {
        this.procedures = new Map();
        this.declaredVars = [];
        if (scriptText != null && javaTrim(scriptText) !== '') {
            new ScriptParser(this, scriptText).parseProgram();
        }
    }

    hasProcedure(name) {
        return name != null && this.procedures.has(name.toLowerCase());
    }

    declaredVariables() {
        return this.declaredVars;
    }

    /** Runs an OnBeforePrint handler. No-op when the template has no such handler. vars: Map. */
    run(procName, vars, objects, expr, baseCtx) {
        if (procName == null) return;
        const body = this.procedures.get(procName.toLowerCase());
        if (!body) return;
        new Interp(vars, objects, expr, baseCtx).execAll(body);
    }
}

class ScriptParser {
    constructor(owner, src) {
        this.owner = owner;
        this.src = src;
        this.i = 0;
    }

    parseProgram() {
        const src = this.src;
        for (;;) {
            this.skipTrivia();
            if (this.i >= src.length) return;
            const word = this.peekWord();
            if (word == null) {
                this.i++;
                continue;
            }
            const lower = word.toLowerCase();
            if (lower === 'var') {
                this.parseVarDecl();
            } else if (lower === 'procedure' || lower === 'function') {
                this.parseProcedure();
            } else if (lower === 'begin') {
                // The trailing main block is always empty in the corpus.
                this.parseBlock([]);
                this.skipTrivia();
                if (this.i < src.length && src[this.i] === '.') this.i++;
            } else {
                this.i += word.length;
            }
        }
    }

    parseVarDecl() {
        const src = this.src;
        this.i += 3; // "var"
        const semi = src.indexOf(';', this.i);
        if (semi < 0) {
            this.i = src.length;
            return;
        }
        const decl = src.substring(this.i, semi);
        const colon = decl.indexOf(':');
        const names = colon < 0 ? decl : decl.substring(0, colon);
        for (const n of names.split(',')) {
            const t = javaTrim(n);
            if (t !== '') this.owner.declaredVars.push(t);
        }
        this.i = semi + 1;
    }

    parseProcedure() {
        const src = this.src;
        const kw = this.peekWord();
        this.i += kw.length;
        this.skipTrivia();
        const name = this.readIdent();
        this.skipTrivia();
        if (this.i < src.length && src[this.i] === '(') this.readBalancedParens();
        this.skipTrivia();
        // Optional ": ReturnType" for functions, then ';'
        const semi = src.indexOf(';', this.i);
        if (semi >= 0) this.i = semi + 1;
        this.skipTrivia();
        const body = [];
        if (this.matchesWord('begin')) this.parseBlock(body);
        this.skipTrivia();
        if (this.i < src.length && src[this.i] === ';') this.i++;
        if (name != null && name !== '') this.owner.procedures.set(name.toLowerCase(), body);
    }

    /** Consumes "begin ... end", filling out. */
    parseBlock(out) {
        const src = this.src;
        this.i += 5; // "begin"
        for (;;) {
            this.skipTrivia();
            if (this.i >= src.length) return;
            if (this.matchesWord('end')) {
                this.i += 3;
                return;
            }
            const s = this.parseStatement();
            if (s != null) out.push(s);
            this.skipTrivia();
            if (this.i < src.length && src[this.i] === ';') this.i++;
        }
    }

    parseStatement() {
        const src = this.src;
        this.skipTrivia();
        if (this.i >= src.length) return null;
        if (this.matchesWord('begin')) {
            // A bare nested block: flatten, order is all that matters.
            const inner = [];
            this.parseBlock(inner);
            if (inner.length === 0) return null;
            return { type: 'if', condText: 'True', thenBody: inner, elseBody: [] };
        }
        if (this.matchesWord('if')) return this.parseIf();
        if (this.matchesWord('end')) return null;

        const start = this.i;
        const target = this.readDottedIdent();
        if (target == null || target === '') {
            this.i = start + 1;
            return null;
        }
        this.skipTrivia();
        if (this.i + 1 < src.length && src[this.i] === ':' && src[this.i + 1] === '=') {
            this.i += 2;
            const a = { type: 'assign', objectName: null, name: null, exprText: '' };
            const dot = target.lastIndexOf('.');
            if (dot > 0) {
                a.objectName = target.substring(0, dot);
                a.name = target.substring(dot + 1);
            } else {
                a.name = target;
            }
            a.exprText = this.readExprUntil(null);
            return a;
        }
        if (this.i < src.length && src[this.i] === '(') {
            const c = { type: 'call', objectName: null, method: null, argExprs: [] };
            const dot = target.lastIndexOf('.');
            if (dot > 0) {
                c.objectName = target.substring(0, dot);
                c.method = target.substring(dot + 1);
            } else {
                c.method = target;
            }
            const args = this.readBalancedParens();
            for (const a of splitArgs(args)) c.argExprs.push(a);
            return c;
        }
        // Unrecognised statement: skip to the end of it rather than guess.
        this.readExprUntil(null);
        return null;
    }

    parseIf() {
        const src = this.src;
        this.i += 2; // "if"
        const node = { type: 'if', condText: '', thenBody: [], elseBody: [] };
        node.condText = this.readExprUntil('then');
        this.skipTrivia();
        if (this.matchesWord('then')) this.i += 4;
        this.skipTrivia();
        if (this.matchesWord('begin')) this.parseBlock(node.thenBody);
        else {
            const s = this.parseStatement();
            if (s != null) node.thenBody.push(s);
        }
        this.skipTrivia();
        // Pascal has no ';' before else, but tolerate one.
        const save = this.i;
        if (this.i < src.length && src[this.i] === ';') {
            this.i++;
            this.skipTrivia();
        }
        if (this.matchesWord('else')) {
            this.i += 4;
            this.skipTrivia();
            if (this.matchesWord('begin')) this.parseBlock(node.elseBody);
            else {
                const s = this.parseStatement();
                if (s != null) node.elseBody.push(s);
            }
        } else {
            this.i = save;
        }
        return node;
    }

    // -- lexical helpers ------------------------------------------------------

    skipTrivia() {
        const src = this.src;
        while (this.i < src.length) {
            const c = src[this.i];
            if (isWhitespace(c)) {
                this.i++;
                continue;
            }
            if (c === '/' && this.i + 1 < src.length && src[this.i + 1] === '/') {
                while (this.i < src.length && src[this.i] !== '\n') this.i++;
                continue;
            }
            if (c === '{') {
                const close = src.indexOf('}', this.i);
                this.i = close < 0 ? src.length : close + 1;
                continue;
            }
            if (c === '(' && this.i + 1 < src.length && src[this.i + 1] === '*') {
                const close = src.indexOf('*)', this.i);
                this.i = close < 0 ? src.length : close + 2;
                continue;
            }
            return;
        }
    }

    peekWord() {
        const src = this.src;
        if (this.i >= src.length) return null;
        if (!isLetter(src[this.i])) return null;
        let j = this.i;
        while (j < src.length && (isLetterOrDigit(src[j]) || src[j] === '_')) j++;
        return src.substring(this.i, j);
    }

    matchesWord(kw) {
        const w = this.peekWord();
        return w != null && w.toLowerCase() === kw.toLowerCase();
    }

    readIdent() {
        const w = this.peekWord();
        if (w == null) return '';
        this.i += w.length;
        return w;
    }

    readDottedIdent() {
        const src = this.src;
        this.skipTrivia();
        const start = this.i;
        while (this.i < src.length) {
            const c = src[this.i];
            if (isLetterOrDigit(c) || c === '_' || c === '.') this.i++;
            else break;
        }
        return src.substring(start, this.i);
    }

    readBalancedParens() {
        const src = this.src;
        this.skipTrivia();
        if (this.i >= src.length || src[this.i] !== '(') return '';
        let depth = 0;
        const start = this.i + 1;
        let inStr = false;
        while (this.i < src.length) {
            const c = src[this.i];
            if (inStr) {
                if (c === '\'') inStr = false;
                this.i++;
                continue;
            }
            if (c === '\'') {
                inStr = true;
                this.i++;
                continue;
            }
            if (c === '(') depth++;
            else if (c === ')') {
                depth--;
                if (depth === 0) {
                    const out = src.substring(start, this.i);
                    this.i++;
                    return out;
                }
            }
            this.i++;
        }
        return '';
    }

    /**
     * Reads an expression up to ';' or 'end'/'else', or up to stopWord.
     * Tracks strings, parentheses and the <field> form so none of those can
     * hide a terminator.
     */
    readExprUntil(stopWord) {
        const src = this.src;
        this.skipTrivia();
        const start = this.i;
        let depth = 0;
        let inStr = false;
        let inField = false;
        while (this.i < src.length) {
            const c = src[this.i];
            if (inStr) {
                if (c === '\'') inStr = false;
                this.i++;
                continue;
            }
            if (inField) {
                if (c === '>') inField = false;
                this.i++;
                continue;
            }
            if (c === '\'') {
                inStr = true;
                this.i++;
                continue;
            }
            if (c === '<' && fieldRefEnd(src, this.i) > 0) {
                inField = true;
                this.i++;
                continue;
            }
            if (c === '(') {
                depth++;
                this.i++;
                continue;
            }
            if (c === ')') {
                if (depth === 0) break;
                depth--;
                this.i++;
                continue;
            }
            if (depth === 0) {
                if (c === ';') break;
                if (isLetter(c)) {
                    const w = this.peekWord();
                    if (w != null) {
                        const lw = w.toLowerCase();
                        if (stopWord != null && lw === stopWord.toLowerCase()) break;
                        if (stopWord == null && (lw === 'end' || lw === 'else')) break;
                        this.i += w.length;
                        continue;
                    }
                }
            }
            this.i++;
        }
        return javaTrim(src.substring(start, this.i));
    }
}

const splitArgs = (s) => {
    const out = [];
    if (s == null || javaTrim(s) === '') return out;
    let depth = 0;
    let inStr = false;
    let cur = '';
    for (let i = 0; i < s.length; i++) {
        const c = s[i];
        if (inStr) {
            cur += c;
            if (c === '\'') inStr = false;
            continue;
        }
        if (c === '\'') {
            inStr = true;
            cur += c;
            continue;
        }
        if (c === '(') depth++;
        if (c === ')') depth--;
        if (c === ',' && depth === 0) {
            out.push(javaTrim(cur));
            cur = '';
            continue;
        }
        cur += c;
    }
    if (cur.length > 0) out.push(javaTrim(cur));
    return out;
};

class Interp {
    constructor(vars, objects, expr, baseCtx) {
        this.vars = vars;
        this.objects = objects;
        this.expr = expr;
        this.ctx = {
            field: (name) => baseCtx.field(name),
            sumField: (name) => baseCtx.sumField(name),
            variable: (name) => {
                if (name == null) return null;
                // Object.Property reads, e.g. lbl3.Top
                const dot = name.lastIndexOf('.');
                if (dot > 0) {
                    const obj = name.substring(0, dot);
                    if (objects.isObject(obj)) return objects.getProperty(obj, name.substring(dot + 1));
                }
                const key = name.toLowerCase();
                if (vars.has(key)) return vars.get(key);
                return baseCtx.variable(name);
            },
        };
    }

    execAll(body) {
        for (const s of body) this.exec(s);
    }

    exec(s) {
        if (s.type === 'assign') {
            const v = this.expr.eval(s.exprText, this.ctx);
            if (s.objectName != null) this.objects.setProperty(s.objectName, s.name, v);
            else this.vars.set(s.name.toLowerCase(), v);
            return;
        }
        if (s.type === 'if') {
            const c = this.expr.eval(s.condText, this.ctx);
            this.execAll(truth(c) ? s.thenBody : s.elseBody);
            return;
        }
        if (s.type === 'call') {
            const args = [];
            for (const a of s.argExprs) {
                try {
                    args.push(this.expr.eval(a, this.ctx));
                } catch (e) {
                    // Argument forms like ExtractFilePath(Application.ExeName)
                    // carry no meaning here; the call itself still does.
                    args.push(a);
                }
            }
            this.objects.invoke(s.objectName, s.method, args);
        }
    }
}

export default Fr3Script;
